import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import axios from "axios";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "../helper/retryOnTransientErrors";

export type FreeSpinGame = {
  provider: string;
  game_id: string;
  game_name: string;
  currency: string;
  bet_factor: number | null;
};

export type FreeSpinGamesFilter = {
  provider?: string;
};

export const FREE_SPIN_GAMES_QUERY_KEY = ["free-spins", "games"] as const;

export const FREESPINS_FILTER_UNAVAILABLE_MESSAGE =
  "Free-spin game filter is unavailable (casino_admin.games_freespins_filter). Enable the flag and retry.";

type RawCasinoGame = {
  provider: string;
  game_id: string;
  name?: string;
  game_name?: string;
  currency?: string;
  bet_factor?: number | null;
  freespins_available?: boolean;
  aggregator?: boolean;
  is_active?: boolean;
};

const PAGE_SIZE = 200;
// Filtered lists are small; keep a modest cap against a stuck has_next.
const MAX_PAGES = 25;

const isValidGame = (item: unknown): item is RawCasinoGame =>
  item !== null &&
  typeof item === "object" &&
  typeof (item as Record<string, unknown>).game_id === "string" &&
  typeof (item as Record<string, unknown>).provider === "string";

const extractItems = (body: unknown): RawCasinoGame[] => {
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  let raw: unknown[] = [];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const nested = (data as Record<string, unknown>).items;
    if (Array.isArray(nested)) raw = nested;
  } else if (Array.isArray(data)) {
    raw = data;
  }
  return raw.filter(isValidGame);
};

/** Prefer root meta (admin games v3); fall back to data.meta. */
const extractHasNext = (body: unknown): boolean | undefined => {
  if (!body || typeof body !== "object") return undefined;
  const root = body as Record<string, unknown>;
  const read = (meta: unknown): boolean | undefined => {
    if (!meta || typeof meta !== "object" || Array.isArray(meta)) return undefined;
    const hasNext = (meta as Record<string, unknown>).has_next;
    return typeof hasNext === "boolean" ? hasNext : undefined;
  };
  const fromRoot = read(root.meta);
  if (fromRoot !== undefined) return fromRoot;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return read((data as Record<string, unknown>).meta);
  }
  return undefined;
};

const fetchGamePages = async (
  filter: FreeSpinGamesFilter | undefined,
  signal: AbortSignal | undefined,
): Promise<RawCasinoGame[]> => {
  // PAM gateway for casino ``GET /casino/api/v1/admin/games`` (CASENG-1039).
  const instance = getPrivateInstance();
  const url = API_ROUTES.GET_ADMIN_GAMES;

  let page = 1;
  let allItems: RawCasinoGame[] = [];

  while (page <= MAX_PAGES) {
    const params: Record<string, string | number | boolean> = {
      page,
      page_size: PAGE_SIZE,
      // Award pickers should only offer live titles.
      active_only: true,
      // Plural ``freespins_only`` (not ``freespin_only``) — singular is ignored
      // by the API and returns the full unfiltered catalog.
      // Requires feature flag casino_admin.games_freespins_filter — 403 when off.
      freespins_only: true,
    };
    if (filter?.provider) params.provider = filter.provider;

    const { data: body } = await instance.get(url, { params, signal });
    const items = extractItems(body);

    if (items.length === 0) break;
    allItems = allItems.concat(items);

    const hasNext = extractHasNext(body);
    if (hasNext === false) break;
    if (hasNext === undefined && items.length < PAGE_SIZE) break;
    page++;
  }

  return allItems;
};

const mapGames = (items: RawCasinoGame[]): FreeSpinGame[] =>
  items.map((g) => ({
    provider: g.provider,
    game_id: g.game_id,
    game_name: g.name || g.game_name || g.game_id,
    currency: g.currency ?? "",
    bet_factor: g.bet_factor ?? null,
  }));

/**
 * Fetches casino admin games for free-spin pickers via
 * ``GET /admin-panel-casino/v3/games`` (gateway for
 * ``GET /casino/api/v1/admin/games``).
 *
 * Requires ``freespins_only=true`` + ``active_only=true``. If the freespins
 * query is forbidden (flag off → 403), fails fast with a clear error instead
 * of scanning the full aggregator catalog page-by-page.
 */
export const fetchFreeSpinGames = async (
  filter?: FreeSpinGamesFilter,
  signal?: AbortSignal,
): Promise<FreeSpinGame[]> => {
  let allItems: RawCasinoGame[];

  try {
    allItems = await fetchGamePages(filter, signal);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      throw new Error(FREESPINS_FILTER_UNAVAILABLE_MESSAGE);
    }
    throw error;
  }

  // Always require explicit freespins_available === true so a misapplied
  // filter (unfiltered 200) cannot leak non–free-spin titles. Drop inactive
  // rows in case active_only is ignored by an older gateway.
  const eligible = allItems.filter(
    (g) => g.freespins_available === true && g.is_active !== false,
  );

  return mapGames(eligible);
};

export const useGetFreeSpinGames = (
  filter?: FreeSpinGamesFilter,
  enabled = true,
): UseQueryResult<FreeSpinGame[], Error> =>
  useQuery<FreeSpinGame[], Error>({
    queryKey: [...FREE_SPIN_GAMES_QUERY_KEY, filter?.provider ?? null],
    queryFn: ({ signal }) => fetchFreeSpinGames(filter, signal),
    enabled: enabled && Boolean(filter?.provider),
    staleTime: 1000 * 60 * 5,
    retry: retryOnTransientErrors,
    refetchOnWindowFocus: false,
  });
